/**
 * Lightweight hover card for Strong's number preview.
 * Shows number, native text, pronunciation, and short definition.
 * Positioned relative to anchor element, flips when near viewport edge.
 */

import { useLayoutEffect, useState, type RefObject } from 'react';
import type { StrongsRef } from '@skjv/common';

interface StrongsPopoverProps {
  data: StrongsRef;
  anchorRef: RefObject<HTMLElement | null>;
}

function usePopoverPosition(anchorRef: RefObject<HTMLElement | null>) {
  const [style, setStyle] = useState<React.CSSProperties>({});

  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const popoverHeight = 160;
    const spaceAbove = rect.top;
    const showAbove = spaceAbove > popoverHeight + 8;

    setStyle({
      position: 'fixed',
      left: `${Math.max(8, Math.min(rect.left, window.innerWidth - 288))}px`,
      ...(showAbove
        ? { bottom: `${window.innerHeight - rect.top + 6}px` }
        : { top: `${rect.bottom + 6}px` }),
      zIndex: 60,
    });
  }, [anchorRef]);

  return style;
}

export function StrongsPopover({ data, anchorRef }: StrongsPopoverProps) {
  const style = usePopoverPosition(anchorRef);

  return (
    <div className="strongs-popover" style={style}>
      <div className="flex items-center gap-2">
        <span className="strongs-popover-number">{data.number}</span>
        {data.language && (
          <span className="strongs-popover-lang">{data.language}</span>
        )}
      </div>
      {data.native && (
        <div className="strongs-popover-native">{data.native}</div>
      )}
      {data.pronunciation && (
        <div className="strongs-popover-pronunciation">{data.pronunciation}</div>
      )}
      {data.shortDefinition && (
        <p className="strongs-popover-definition">{data.shortDefinition}</p>
      )}
      <p className="strongs-popover-hint">Click for full definition</p>
    </div>
  );
}
